import * as React from 'react'
import * as ReactDOM from 'react-dom'

type PortalPopoverProps = {
  isOpen: boolean
  targetId: string
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
  onClickOutside?: () => void
}

/**
 * A portal-based popover that always renders centered on screen.
 * Replaces reactstrap 6.x Popover which used the removed
 * ReactDOM.unstable_renderSubtreeIntoContainer (broken in React 18).
 */
export const PortalPopover: React.FC<PortalPopoverProps> = ({
  isOpen,
  className,
  style,
  children,
  onClickOutside,
  targetId,
}) => {
  const popoverRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isOpen || !onClickOutside) return undefined

    const handleMouseDown = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        const target = document.getElementById(targetId)
        if (target && target.contains(e.target as Node)) return

        onClickOutside()
      }
    }

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleMouseDown)
    }, 0)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [isOpen, onClickOutside, targetId])

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.25)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && onClickOutside) {
          onClickOutside()
        }
      }}
    >
      <div
        ref={popoverRef}
        className={`tw-bg-base-100 ${className ?? ''}`.trim()}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          zIndex: 9999,
          boxShadow: '0px 10px 30px -5px rgba(0,0,0,0.3)',
          borderRadius: 8,
          maxHeight: 'calc(100vh - 32px)',
          maxWidth: 'calc(100vw - 32px)',
          overflowY: 'auto',
          ...style,
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
